import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useTrial } from '../contexts/TrialContext';
import '../styles/CourtyardLobby.css';
import FriendChat from './FriendChat';
import LobbyChat from './LobbyChat';
import SkinStore from './SkinStore';
import StatsDashboard from './StatsDashboard';
import Leaderboard from './Leaderboard';
import LobbyTutorial, { shouldShowTutorial } from './LobbyTutorial';
import NotificationSettings from './NotificationSettings';

const CourtyardLobby = ({ onNavigate, onOpenSettings, onOpenAssistant, onOpenTroubleshooter, addGlobalNotification, onDukeSkinSelect }) => {
  const { t } = useTranslation();
  const { isTrialActive, hoursRemaining, timeRemainingFormatted } = useTrial();
  const [showFriends, setShowFriends] = useState(false);
  const [showLobbyChat, setShowLobbyChat] = useState(false);
  const [showDukeStore, setShowDukeStore] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showWatcher, setShowWatcher] = useState(false);

  // "Something is watching you" — appears every other login
  useEffect(() => {
    if (sessionStorage.getItem('watcher_counted')) return;
    sessionStorage.setItem('watcher_counted', '1');
    const count = parseInt(localStorage.getItem('watcher_login_count') || '0', 10) + 1;
    localStorage.setItem('watcher_login_count', String(count));
    if (count % 2 === 0) {
      setShowWatcher(true);
      const timer = setTimeout(() => setShowWatcher(false), 13500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Check if tutorial should be shown on first mount
  useEffect(() => {
    // Small delay to ensure lobby is rendered before showing tutorial
    const timer = setTimeout(() => {
      if (shouldShowTutorial()) {
        setShowTutorial(true);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleTutorialComplete = useCallback(() => {
    setShowTutorial(false);
  }, []);

  // Handle unread count updates from FriendChat
  const handleUnreadCountChange = useCallback((count) => {
    setUnreadMessageCount(count);
  }, []);

  return (
    <div className="courtyard-lobby">
      {showWatcher && (
        <div className="watcher-message" data-testid="watcher-message">
          <span className="watcher-text">
            <span className="watcher-flicker">Something is watching you...</span>
          </span>
        </div>
      )}
      <div className="courtyard-background">
        <img 
          src="https://customer-assets.emergentagent.com/job_kingdom-chess-1/artifacts/27qp7gkp_downloadlobby%20%2843%29.jpg" 
          alt="Courtyard Lobby" 
          className="courtyard-image"
        />
        
        <div className="courtyard-overlay">
          <div className="courtyard-header">
            <h1 className="courtyard-title">{t('lobby.title')}</h1>
            <h2 className="courtyard-subtitle">
              <span className="subtitle-text">{t('cover.riseOfDukes', 'Rise of the Dukes')}</span>
            </h2>
          </div>
          
          {/* Trial Banner */}
          {isTrialActive && hoursRemaining < 999999 && (
            <div className="trial-banner" data-testid="trial-banner">
              <div className="trial-banner-glow" />
              <span className="trial-badge">FREE TRIAL</span>
              <span className="trial-text">Full Access — {timeRemainingFormatted} remaining</span>
            </div>
          )}
          
          {/* Main Action Buttons - Centered */}
          <div className="main-actions">
            <button 
              className="main-action-btn quick-play-btn"
              onClick={() => onNavigate('quickplay')}
              title={t('lobby.quickPlay')}
              data-testid="quick-play-btn"
            >
              <span className="action-icon">⚡</span>
              <div className="action-content">
                <span className="action-title">{t('lobby.quickPlay')}</span>
                <span className="action-subtitle">{t('game.fightToBeKing')} vs AI</span>
              </div>
            </button>
            
            <button 
              className="main-action-btn game-setup-btn"
              onClick={() => onNavigate('newgame')}
              title={t('lobby.gameSetup', 'Game Setup')}
              data-testid="game-setup-btn"
            >
              <span className="action-icon">🎮</span>
              <div className="action-content">
                <span className="action-title">{t('lobby.gameSetup', 'GAME SETUP')}</span>
                <span className="action-subtitle">{t('lobby.chooseVariant', 'Choose Variant & Mode')}</span>
              </div>
            </button>
            
            <button 
              className="main-action-btn multiplayer-btn"
              onClick={() => onNavigate('multiplayer')}
              title={t('lobby.multiplayer', 'Multiplayer')}
              data-testid="multiplayer-btn"
            >
              <span className="action-icon">🌐</span>
              <div className="action-content">
                <span className="action-title">{t('lobby.multiplayer', 'MULTIPLAYER')}</span>
                <span className="action-subtitle">{t('lobby.onlineMatches', 'Online Matches')}</span>
              </div>
            </button>
            
            <button 
              className="main-action-btn tournament-btn"
              onClick={() => onNavigate('tournaments')}
              title={t('lobby.tournaments', 'Tournaments')}
              data-testid="tournaments-btn"
            >
              <span className="action-icon">🏆</span>
              <div className="action-content">
                <span className="action-title">{t('lobby.tournaments', 'TOURNAMENTS')}</span>
                <span className="action-subtitle">{t('lobby.competeBadges', 'Compete & Win Badges')}</span>
              </div>
            </button>
            
            <button 
              className="main-action-btn ranked-btn"
              onClick={() => onNavigate('ranked')}
              title={t('lobby.ranked', 'Ranked')}
              data-testid="ranked-btn"
            >
              <span className="action-icon">⚔️</span>
              <div className="action-content">
                <span className="action-title">{t('lobby.ranked', 'RANKED')}</span>
                <span className="action-subtitle">{t('lobby.climbLeaderboard', 'Climb the Leaderboard')}</span>
              </div>
            </button>
          </div>
          
          {/* Secondary Options - Bottom Center */}
          <div className="secondary-actions">
            <button 
              className="secondary-btn"
              onClick={() => onNavigate('rules')}
              title={t('game.rules')}
              data-testid="rules-btn"
            >
              <span className="btn-icon">📖</span>
              <span className="btn-text">{t('game.rules')}</span>
            </button>
            
            <button 
              className="secondary-btn"
              onClick={() => setShowLobbyChat(true)}
              title={t('lobby.chat')}
              data-testid="chat-btn"
            >
              <span className="btn-icon">💬</span>
              <span className="btn-text">{t('lobby.chat')}</span>
            </button>
            
            <button 
              className="secondary-btn store-btn"
              onClick={() => setShowDukeStore(true)}
              title={t('lobby.skins')}
              data-testid="skins-btn"
            >
              <span className="btn-icon">🛡️</span>
              <span className="btn-text">{t('lobby.skins')}</span>
            </button>
            
            <button 
              className="secondary-btn"
              onClick={() => onOpenSettings()}
              title={t('lobby.settings')}
              data-testid="settings-btn"
            >
              <span className="btn-icon">⚙️</span>
              <span className="btn-text">{t('lobby.settings')}</span>
            </button>
          </div>
          
          {/* User & Utility Sidebar */}
          <div className="lobby-sidebar">
            <div className="sidebar-section user-section">
              <button 
                className="sidebar-btn profile-btn"
                onClick={() => onNavigate('profile')}
                title={t('lobby.profile', 'Profile')}
                data-testid="profile-btn"
              >
                <span className="sidebar-icon">👤</span>
                <span className="sidebar-text">{t('lobby.profile', 'Profile')}</span>
              </button>
              
              <button 
                className="sidebar-btn friends-btn"
                onClick={() => setShowFriends(true)}
                title={t('lobby.friends')}
                data-testid="friends-btn"
              >
                <span className="sidebar-icon">👥</span>
                <span className="sidebar-text">{t('lobby.friends')}</span>
              </button>
              
              <button 
                className="sidebar-btn messages-btn"
                onClick={() => setShowFriends(true)}
                title={t('lobby.messages', 'Messages')}
                data-testid="messages-btn"
              >
                <span className="sidebar-icon">💬</span>
                <span className="sidebar-text">{t('lobby.messages', 'Messages')}</span>
                {unreadMessageCount > 0 && (
                  <span className="messages-badge">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>
                )}
              </button>
              
              <button 
                className="sidebar-btn stats-btn"
                onClick={() => setShowStats(true)}
                title="Statistics"
                data-testid="stats-btn"
              >
                <span className="sidebar-icon">📊</span>
                <span className="sidebar-text">Stats</span>
              </button>
              
              <button 
                className="sidebar-btn leaderboard-btn"
                onClick={() => setShowLeaderboard(true)}
                title="Leaderboards"
                data-testid="leaderboard-btn"
              >
                <span className="sidebar-icon">🏆</span>
                <span className="sidebar-text">Leaderboards</span>
              </button>
              
              <button 
                className="sidebar-btn subscription-btn"
                onClick={() => onNavigate('subscription')}
                title="Subscription &amp; Membership"
                data-testid="subscription-btn"
              >
                <span className="sidebar-icon">👑</span>
                <span className="sidebar-text">Subscription</span>
              </button>
              
              <button 
                className="sidebar-btn notifications-btn"
                onClick={() => setShowNotifications(true)}
                title={t('lobby.notifications', 'Notifications')}
                data-testid="notifications-btn"
              >
                <span className="sidebar-icon">🔔</span>
                <span className="sidebar-text">{t('lobby.notifications', 'Notifications')}</span>
              </button>
            </div>
            
            <div className="sidebar-divider"></div>
            
            <div className="sidebar-section help-section">
              <button 
                className="sidebar-btn"
                onClick={onOpenAssistant}
                title={t('lobby.assistant', 'Assistant')}
                data-testid="assistant-btn"
              >
                <span className="sidebar-icon">🤖</span>
                <span className="sidebar-text">{t('lobby.assistant', 'Assistant')}</span>
              </button>
              
              <button 
                className="sidebar-btn"
                onClick={onOpenTroubleshooter}
                title={t('lobby.help', 'Help')}
                data-testid="help-btn"
              >
                <span className="sidebar-icon">🔧</span>
                <span className="sidebar-text">{t('lobby.help', 'Help')}</span>
              </button>
            </div>
            
            <div className="sidebar-divider"></div>
            
            <button 
              className="sidebar-btn exit-btn"
              onClick={() => onNavigate('cover')}
              title={t('lobby.exit', 'Exit')}
              data-testid="exit-btn"
            >
              <span className="sidebar-icon">🚪</span>
              <span className="sidebar-text">{t('lobby.exit', 'Exit')}</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Lobby Chat Modal (Public Live Chat) */}
      {showLobbyChat && (
        <LobbyChat 
          isOpen={showLobbyChat}
          onClose={() => setShowLobbyChat(false)}
        />
      )}
      
      {/* Skin Store Modal */}
      <SkinStore 
        isOpen={showDukeStore}
        onClose={() => setShowDukeStore(false)}
        onSkinSelect={onDukeSkinSelect}
      />
      
      {/* Friends & Private Chat Modal (Consolidated Social Component) */}
      <FriendChat 
        isOpen={showFriends}
        onClose={() => setShowFriends(false)}
        onUnreadCountChange={handleUnreadCountChange}
        addGlobalNotification={addGlobalNotification}
      />
      
      {/* Statistics Dashboard Modal */}
      <StatsDashboard 
        isOpen={showStats}
        onClose={() => setShowStats(false)}
      />
      
      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <Leaderboard
          onClose={() => setShowLeaderboard(false)}
        />
      )}
      
      {/* First-time User Tutorial */}
      {showTutorial && (
        <LobbyTutorial onComplete={handleTutorialComplete} />
      )}
      
      {/* Notifications Settings Modal */}
      {showNotifications && (
        <div className="notifications-modal-overlay" onClick={() => setShowNotifications(false)}>
          <div className="notifications-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notifications-modal-header">
              <h2>🔔 Push Notifications</h2>
              <button className="close-btn" onClick={() => setShowNotifications(false)}>✕</button>
            </div>
            <div className="notifications-modal-content">
              <NotificationSettings />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourtyardLobby;
